import * as gateway from 'sfx-web-contract';
import { AlkLogger } from '@alk/core';
import { AUTHENTICATION_FACTORY, IAuthenticationFactory } from '../authentication';
import { createStreamData } from './communication.utils';
import { DATA_TRANSFER_SERVICE, IDataTransferService } from '../data-transfer';
import { DeepRequired } from '../../types';
import { filter, finalize, map, share, takeUntil, tap } from 'rxjs/operators';
import { ICommunicationService } from './communication.interface';
import { inject, injectable } from 'inversify';
import { IStreamData } from '../../interfaces';
import { Observable, Subject } from 'rxjs';
import { uInt8ArrayToLog } from '../../helpers/uint8array-utils';

@injectable()
export class CommunicationService implements ICommunicationService {

    @inject(DATA_TRANSFER_SERVICE) private readonly dataTransferService: IDataTransferService;
    @inject(AUTHENTICATION_FACTORY) private readonly authFactory: IAuthenticationFactory;

    private currentRequestId: number;

    /**
     * subsriptions list. key is request Id, value it type of message
     */
    private readonly subscriptions: Map<number, number> = new Map<number, number>();

    private dataFeedObs$: Observable<gateway.EnvelopeResponse>;

    private readonly destroy$: Subject<boolean> = new Subject<boolean>();

    delete(subscriptionId: number): void {

        if (!this.subscriptions.has(subscriptionId)) {
            throw Error(`[COMMUNICATION_SERVICE#delete] unable to unsubscribe from not presented subscription ${ subscriptionId }`);
        }

        const envelop = gateway.EnvelopeRequest.create({
            requestId: subscriptionId,
            requestType: gateway.RequestType.REQUESTTYPE_UNSUBSCRIBE,
        });

        const message2send = gateway.EnvelopeRequest.encode(envelop).finish();

        const subInfo = `[${ subscriptionId },${ this.subscriptions.get(subscriptionId) }]`;
        AlkLogger.debug(`[COMMUNICATION_SERVICE#delete] message to send to unsubscribe ${ subInfo } : ${ uInt8ArrayToLog(message2send) }`);

        this.dataTransferService.send(message2send);

        this.subscriptions.delete(subscriptionId);

        AlkLogger.debug(`[COMMUNICATION_SERVICE#delete] subscription ${ subscriptionId } deleted. ${ ''
        }List of active subscriptions: ${ JSON.stringify([ ...this.subscriptions ]) }`);
    }

    subscriptionsList(): Array<number> {
        return [ ...this.subscriptions.keys() ];
    }

    send(message: Uint8Array, nestedMsgType: number): void {
        this.sendMessageInternal(message, nestedMsgType);
    }

    create(message: Uint8Array, nestedMsgType: number): number {
        const requestId = this.sendMessageInternal(message, nestedMsgType);

        this.subscriptions.set(requestId, nestedMsgType);

        AlkLogger.debug(`[COMMUNICATION_SERVICE#create] List of active subscriptions: ${ JSON.stringify([ ...this.subscriptions ]) }`);

        return requestId;
    }

    private sendMessageInternal(message: Uint8Array, nestedMsgType: number): number {
        const requestId = this.generateRequestId();

        const requestType = gateway.RequestType.REQUESTTYPE_SUBSCRIBE;
        const nestedMsg = gateway.google.protobuf.BytesValue.create({
            value: message,
        });
        const envelop = gateway.EnvelopeRequest.create({
            requestId: requestId,
            requestType: requestType,
            nestedMsgType: nestedMsgType,
            nestedMessage: nestedMsg,
        });

        const dataEnvelop = gateway.EnvelopeRequest.encode(envelop).finish();

        AlkLogger.debug(`[COMMUNICATION_SERVICE#sendMessageInternal] message to send ${ uInt8ArrayToLog(dataEnvelop) }`);

        this.dataTransferService.send(dataEnvelop);

        AlkLogger.debug(`[COMMUNICATION_SERVICE#sendMessageInternal] subscription ${ ''
        }created for type ${ nestedMsgType } (${ gateway.MessageTypes[ nestedMsgType ] }) with request id: ${ requestId }.`);

        return requestId;
    }

    private dataFeed(): Observable<gateway.EnvelopeResponse> {

        return Boolean(this.dataFeedObs$) ?
            this.dataFeedObs$ :
            this.dataFeedObs$ = this.dataTransferService.messageObserver()
                .pipe(
                    takeUntil(this.destroy$),
                    tap(function(data) {
                        AlkLogger.debug(`[COMMUNICATION_SERVICE#dataFeed] received data: ${ uInt8ArrayToLog(data) })`);
                    }),
                    map((data) => gateway.EnvelopeResponse.decode(data)),
                    tap((envelop) => {
                        AlkLogger.debug(`[COMMUNICATION_SERVICE#dataFeed] received envelop: ${ JSON.stringify(envelop.toJSON()) }`);
                    }),
                    finalize(() => this.onDestroy),
                    share());

    }

    private onDestroy() {

        AlkLogger.debug('[COMMUNICATION_SERVICE#dataFeed] data feed is destroyed');

        this.subscriptions.clear();
    }

    dataObservable(type: number): Observable<IStreamData<Uint8Array>> {

        AlkLogger.debug(`[COMMUNICATION_SERVICE#dataObservable] called for type ${ type }`);

        return this.dataFeed().pipe(
            filter(envelop => this.subscriptions.get(envelop.requestId) === type),
            finalize(() => AlkLogger.debug(`[COMMUNICATION_SERVICE] feed complete for type: ${ type }`)),
            map(createStreamData));
    }

    private sendFirstMessage(): void {
        const authSrv = this.authFactory();

        const message: DeepRequired<gateway.IAuthenticationRequest> = {
            Token: authSrv.getToken().access_token,
        };

        this.send(
            gateway.AuthenticationRequest.encode(message).finish(),
            gateway.MessageTypes.Authentication,
        );

        AlkLogger.debug('[COMMUNICATION_SERVICE#sendFirstMessage] first message sent');
    }

    async start(): Promise<void> {
        this.currentRequestId = Math.floor(Math.random() * 100) * 1000;

        AlkLogger.debug(`[COMMUNICATION_SERVICE#start] new session started with ${ this.currentRequestId }`);

        await this.dataTransferService.open();
        this.sendFirstMessage();
    }

    stop(): void {

        AlkLogger.info(`[COMMUNICATION_SERVICE#stop] session stoped`);

        if (Boolean(this.dataFeedObs$)) {
            this.dataFeedObs$ = null;
            delete(this.dataFeedObs$);
        }

        this.destroy$.next(true);

        this.dataTransferService.close();
    }

    generateRequestId(): number {
        this.currentRequestId += 1;

        return this.currentRequestId;
    }
}
